export const viewportStyle = {
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
};

const LASER_CURSOR_SIZE_PX = 6;
const LASER_TRAIL_FADE_MS = 800;
const LASER_SAMPLE_MIN_DISTANCE = 2;
const LASER_SAMPLE_MIN_INTERVAL_MS = 8;
const LASER_TRAIL_CORE_WIDTH_PX = 4;
const LASER_TRAIL_GLOW_WIDTH_PX = 10;
const LASER_TRAIL_GLOW_BLUR_PX = 16;

export const LASER_CANVAS_STYLE = {
    height: '100%',
    inset: 0,
    pointerEvents: 'none',
    position: 'absolute',
    width: '100%',
    zIndex: 2,
};

export const LASER_CURSOR_STYLE = {
    backgroundColor: 'rgba(217, 45, 32, 0.92)',
    borderRadius: '50%',
    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.45), 0 0 12px rgba(217, 45, 32, 0.72)',
    height: `${LASER_CURSOR_SIZE_PX}px`,
    left: '0px',
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: '0px',
    transform: 'translate(-50%, -50%)',
    transition: 'opacity 120ms ease',
    width: `${LASER_CURSOR_SIZE_PX}px`,
    zIndex: 3,
};

export function createEmptyLaserTrailState() {
    return {
        activeStroke: [],
        fadingStrokes: [],
    };
}

export function hideLaserCursor(cursorElement) {
    if (!cursorElement) {
        return;
    }

    const targetCursorElement = cursorElement;
    targetCursorElement.style.opacity = '0';
}

export function positionLaserCursor(cursorElement, localX, localY) {
    if (!cursorElement) {
        return;
    }

    const targetCursorElement = cursorElement;
    targetCursorElement.style.left = `${localX}px`;
    targetCursorElement.style.top = `${localY}px`;
    targetCursorElement.style.opacity = '1';
}

export function clearLaserCanvas(laserCanvasContextRef, laserCanvasRef) {
    const laserCanvasContext = laserCanvasContextRef.current;
    const laserCanvas = laserCanvasRef.current;

    if (!laserCanvasContext || !laserCanvas) {
        return;
    }

    laserCanvasContext.clearRect(0, 0, laserCanvas.clientWidth, laserCanvas.clientHeight);
}

export function resizeLaserCanvas(laserCanvas) {
    if (!laserCanvas) {
        return null;
    }

    const targetLaserCanvas = laserCanvas;
    const width = targetLaserCanvas.clientWidth;
    const height = targetLaserCanvas.clientHeight;
    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const nextWidth = Math.max(Math.round(width * devicePixelRatio), 1);
    const nextHeight = Math.max(Math.round(height * devicePixelRatio), 1);

    if (targetLaserCanvas.width !== nextWidth || targetLaserCanvas.height !== nextHeight) {
        targetLaserCanvas.width = nextWidth;
        targetLaserCanvas.height = nextHeight;
    }

    const laserCanvasContext = targetLaserCanvas.getContext('2d');

    if (!laserCanvasContext) {
        return null;
    }

    const targetLaserCanvasContext = laserCanvasContext;

    targetLaserCanvasContext.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    targetLaserCanvasContext.lineCap = 'round';
    targetLaserCanvasContext.lineJoin = 'round';

    return targetLaserCanvasContext;
}

export function drawLaserStroke(laserCanvasContext, points, opacity) {
    if (!laserCanvasContext || points.length < 2 || opacity <= 0) {
        return;
    }

    const targetLaserCanvasContext = laserCanvasContext;
    const drawPath = () => {
        targetLaserCanvasContext.beginPath();
        targetLaserCanvasContext.moveTo(points[0].x, points[0].y);

        for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
            const point = points[pointIndex];
            targetLaserCanvasContext.lineTo(point.x, point.y);
        }
    };

    targetLaserCanvasContext.save();

    drawPath();
    targetLaserCanvasContext.strokeStyle = `rgba(217, 45, 32, ${0.3 * opacity})`;
    targetLaserCanvasContext.lineWidth = LASER_TRAIL_GLOW_WIDTH_PX;
    targetLaserCanvasContext.shadowBlur = LASER_TRAIL_GLOW_BLUR_PX;
    targetLaserCanvasContext.shadowColor = `rgba(217, 45, 32, ${0.85 * opacity})`;
    targetLaserCanvasContext.stroke();

    drawPath();
    targetLaserCanvasContext.shadowBlur = 0;
    targetLaserCanvasContext.strokeStyle = `rgba(255, 48, 48, ${0.95 * opacity})`;
    targetLaserCanvasContext.lineWidth = LASER_TRAIL_CORE_WIDTH_PX;
    targetLaserCanvasContext.stroke();

    drawPath();
    targetLaserCanvasContext.strokeStyle = `rgba(255, 232, 232, ${0.55 * opacity})`;
    targetLaserCanvasContext.lineWidth = 1.5;
    targetLaserCanvasContext.stroke();

    targetLaserCanvasContext.restore();
}

export function clearLaserTrail(laserTrailRef, laserPointerStateRef) {
    const targetLaserTrailRef = laserTrailRef;
    const targetLaserPointerState = laserPointerStateRef.current;

    targetLaserTrailRef.current = createEmptyLaserTrailState();
    targetLaserPointerState.lastSampleAt = 0;
    targetLaserPointerState.lastSamplePosition = null;
}

export function resetLaserPointerState(laserPointerStateRef) {
    const targetLaserPointerState = laserPointerStateRef.current;

    targetLaserPointerState.inside = false;
    targetLaserPointerState.isPressed = false;
    targetLaserPointerState.lastSampleAt = 0;
    targetLaserPointerState.lastSamplePosition = null;
    targetLaserPointerState.localX = 0;
    targetLaserPointerState.localY = 0;
}

export function getAppearanceForModel(appearance, modelId) {
    const defaultAppearance = appearance?.defaultAppearance || appearance || {
        showTexture: true,
    };

    return appearance?.modelAppearances?.[modelId] || defaultAppearance;
}

export function getLaserConfig() {
    return {
        fadeMs: LASER_TRAIL_FADE_MS,
        sampleMinDistance: LASER_SAMPLE_MIN_DISTANCE,
        sampleMinIntervalMs: LASER_SAMPLE_MIN_INTERVAL_MS,
    };
}
